import { Typography } from '@mui/material';
import { filter, map } from 'es-toolkit/compat';
import React from 'react';

import { Orchestration } from 'api/experimentResults';
import Loader from 'components/Loader';
import { ExportScopeField, ExportScopeValue } from 'components/ResultsExport/ExportFormFields';
import { Study } from 'interfaces/study';
import { useCurrentLabId } from 'utils/useCurrentLab';
import {
  getWorkflowSourceAvailability,
  WorkflowExportKind,
  WorkflowResultsSource,
  WorkflowResultsSourceSelection,
  workflowResultsCheckingMessage,
  workflowRunsFailedNote,
} from 'services/export/workflowExportSource';
import { WorkflowRunsErrorAlert } from './WorkflowRunsErrorAlert';

interface WorkflowResultsSourceSelectProps {
  kind: WorkflowExportKind;
  study: Study;
  selection: WorkflowResultsSourceSelection;
  onChange: (selection: WorkflowResultsSourceSelection) => void;
  runs: Orchestration[];
  // A leftover study-wide run list in the link. It is not applied; each panel chooses its own runs.
  ignoredStudyRunCount?: number;
  isLoadingRuns: boolean;
  hasRunsError?: boolean;
  // Why the runs could not be loaded; shown with its HTTP status and support details.
  runsError?: unknown;
}

const sourceOptions: { source: WorkflowResultsSource; label: string }[] = [
  { source: 'published', label: 'Published results' },
  { source: 'approved', label: 'Approved results (not yet published)' },
  { source: 'latest', label: "Each slide's latest run" },
  { source: 'run', label: 'A specific post-processing run' },
];

// Without published or approved results there is nothing to choose between, so only the run-based choices are offered.
const filterOptions = (options: typeof sourceOptions, runsOnly: boolean) =>
  runsOnly ? filter(options, ({ source }) => source === 'latest' || source === 'run') : options;

const describeIgnoredStudyRuns = (count: number) =>
  count === 1
    ? '1 run in this link was chosen for the whole study and is not applied. Choose runs on each panel.'
    : `${count} runs in this link were chosen for the whole study and are not applied. Choose runs on each panel.`;

// Only rendered for users who may view unpublished results. Until the runs are known no option exists to choose,
// so a source is never picked from a partial picture. Specific runs are chosen on each panel, not here.
export const WorkflowResultsSourceSelect: React.FC<WorkflowResultsSourceSelectProps> = ({
  kind,
  study,
  selection,
  onChange,
  runs,
  ignoredStudyRunCount = 0,
  isLoadingRuns,
  hasRunsError = false,
  runsError,
}) => {
  const { labId } = useCurrentLabId();
  if (isLoadingRuns) {
    return (
      <ExportScopeValue label="Results for all slides">
        <Loader inline message={workflowResultsCheckingMessage} />
      </ExportScopeValue>
    );
  }

  const availabilityOf = (source: WorkflowResultsSource) =>
    getWorkflowSourceAvailability({
      kind,
      study,
      selection: { source },
      runCount: runs.length,
      canViewUnpublishedResults: true,
    });
  const runsOnly = availabilityOf('published').disabled && availabilityOf('approved').disabled;

  const runsErrorAlert = hasRunsError ? (
    <WorkflowRunsErrorAlert kind={kind} studyId={study.id} labId={labId} error={runsError} />
  ) : null;

  return (
    <ExportScopeField
      id="workflow-export-results"
      label="Results for all slides"
      options={map(filterOptions(sourceOptions, runsOnly), ({ source, label }) => {
        const { disabled, note } = availabilityOf(source);
        return {
          value: source,
          label,
          disabled,
          note: disabled
            ? (source === 'run' || source === 'latest') && hasRunsError
              ? workflowRunsFailedNote
              : note
            : undefined,
        };
      })}
      value={selection.source}
      onChange={(source) => onChange({ source: source as WorkflowResultsSource })}
    >
      {runsErrorAlert}
      {ignoredStudyRunCount > 0 && (
        <Typography variant="body2" role="status">
          {describeIgnoredStudyRuns(ignoredStudyRunCount)}
        </Typography>
      )}
    </ExportScopeField>
  );
};
